import { Box, Flex, Spacer, Stack, useMediaQuery } from '@chakra-ui/react';
import { ProductSuggestionsData } from '@diamond/shared/types';
import { Button, IconButton, Text } from '@diamond/shared/ui';
// import {
//   TRACKING_PAGES,
//   useProductListTracking,
// } from '@diamond/sol-buyer/constant';
import { useShipToStore } from '@diamond/sol-buyer-context';
import { ArrowBackOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import { useRouter } from 'next/router';
import { useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import shallow from 'zustand/shallow';

import { CardProduct } from '../common';

export interface ProductSliderProps {
  title: string;
  data: ProductSuggestionsData;
  index: number;
  handleClickSeeAll: () => void;
  handleTrackEvent?: () => void;
  section?: string;
  pageName?: string;
}
const SLIDES_TO_SHOW = 6;

export function ProductSlider({
  title,
  data,
  index,
  section,
  pageName,
  handleClickSeeAll,
  handleTrackEvent,
}: ProductSliderProps) {
  const [isMobile] = useMediaQuery('(max-width: 992px)');
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );
  const [page, setPage] = useState(0);
  const { push } = useRouter();
  const hookForm = useForm();

  const tittleHereWrapper = useRef(null);

  function handleClickProductCard(productId: string) {
    handleTrackEvent?.();
    push({
      pathname: `/product/${productId}`,
      query: { warehouse: selectedShipToId },
    });
  }

  function sideScrollProduct(distance: number, step: number) {
    let scrollAmount = 0;
    const element = tittleHereWrapper.current as unknown as HTMLElement;
    const slideTimer = setInterval(() => {
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);
      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
    }, 25);
  }

  const productsData = useMemo(() => (data !== undefined ? data : []), [data]);

  const isHidden = productsData.length > 0 ? false : true;

  // Track VIEW_ITEM_LIST untuk product slider dengan dynamic section dan page name
  // useProductListTracking(
  //   productsData,
  //   {
  //     category_lv1: section || title,
  //   },
  //   !isHidden,
  //   {
  //     sectionName: section,
  //     pageName: pageName || TRACKING_PAGES.BERANDA,
  //     sectionId: title, // Use title as sectionId for Product Highlight sections
  //   }
  // );

  return (
    <FormProvider {...hookForm}>
      {!isHidden && (
        <Box>
          <Flex>
            <Box>
              <Text
                as="span"
                fontSize={{ base: '22px', md: '34px' }}
                mr="4"
                data-testid="product-slider-title"
              >
                {title}
              </Text>
              <Button
                variant="link"
                display={{ base: 'none', md: 'contents' }}
                color="blue"
                onClick={() => handleClickSeeAll()}
                data-testid="see-all-button"
              >
                Lihat Semua
              </Button>
            </Box>
            <Spacer />
            <Button
              variant="link"
              display={{ base: 'contents', md: 'none' }}
              color="blue"
              onClick={() => handleClickSeeAll()}
            >
              Lihat Semua
            </Button>
            <Stack
              spacing="4"
              direction="row"
              display={{ base: 'none', lg: 'contents' }}
            >
              <IconButton
                aria-label="back-icon"
                name="back-icon"
                icon={<ArrowBackOutlined fontSize="small" />}
                variant="outline"
                borderWidth="1px"
                borderColor="blue"
                color="blue"
                isDisabled={page === 0}
                onClick={() => {
                  if (page > 0) {
                    sideScrollProduct(100, -320);
                    setPage(page - SLIDES_TO_SHOW);
                  }
                }}
              />
              <IconButton
                aria-label="next-icon"
                name="next-icon"
                icon={<ArrowForwardOutlined fontSize="small" />}
                variant="outline"
                borderWidth="1px"
                borderColor="blue"
                color="blue"
                isDisabled={page >= productsData.length - SLIDES_TO_SHOW}
                onClick={() => {
                  if (page < productsData.length - SLIDES_TO_SHOW) {
                    sideScrollProduct(100, 320);
                    setPage(page + SLIDES_TO_SHOW);
                  }
                }}
              />
            </Stack>
          </Flex>
          <Box
            id={`container-highlight-${index}`}
            p={1}
            ref={tittleHereWrapper}
          >
            {isMobile ? (
              <Stack
                direction="row"
                spacing={{ base: 4, md: 4 }}
                w="full"
                px={0}
                pb="6"
                mt={{ base: 4, md: 0 }}
                overflow="auto"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {productsData
                  .map((val, idx) => {
                    const price =
                      val.prices.length > 0 ? val.prices[0].price : 0;
                    return (
                      <Box key={idx} flex="none" width="220px">
                        <CardProduct
                          productId={val.id}
                          imageUrl={val.product_images_medium?.[0]}
                          productName={val.brand_desc}
                          itemCode={val.item_code}
                          price={price}
                          unit={val.pack_unit}
                          description={val.title}
                          measurement_unit={val.sales_unit}
                          key={val.id}
                          name="product"
                          navigateTo={{
                            pathname: `/product/${val.id}`,
                            query: { warehouse: selectedShipToId },
                          }}
                          onClickTambahKeranjang={() => {
                            handleClickProductCard(val.id);
                          }}
                          stockStatus={
                            val.stock > 0 ? 'AVAILABLE' : 'OUT_OF_STOCK'
                          }
                          product={val}
                          section={section}
                          page={pageName}
                        />
                      </Box>
                    );
                  })
                  .slice(page, page + 10)}
              </Stack>
            ) : (
              <Box
                display="grid"
                gridTemplateColumns={`repeat(${SLIDES_TO_SHOW}, 1fr)`}
                gap="2"
                overflow="auto"
                pb="4"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {productsData
                  .map((val, idx) => {
                    const price =
                      val.prices.length > 0 ? val.prices[0].price : 0;
                    return (
                      <Box key={idx} flexBasis={{ base: 'auto', md: '25%' }}>
                        <CardProduct
                          productId={val.id}
                          imageUrl={val.product_images_medium?.[0]}
                          productName={val.brand_desc}
                          itemCode={val.item_code}
                          price={price}
                          unit={val.pack_unit}
                          description={val.title}
                          measurement_unit={val.sales_unit}
                          key={val.id}
                          name="product"
                          navigateTo={{
                            pathname: `/product/${val.id}`,
                            query: { warehouse: selectedShipToId },
                          }}
                          onClickTambahKeranjang={() => {
                            handleClickProductCard(val.id);
                          }}
                          stockStatus={val.stock_status}
                          stock={val.stock}
                          product={val}
                        />
                      </Box>
                    );
                  })
                  .slice(page, page + SLIDES_TO_SHOW)}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </FormProvider>
  );
}
